import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Clock, Coffee, LogOut, MessageCircle, PackageX, Power, PowerOff, ShoppingCart, Users } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { CloseShiftButton } from "@/components/admin/CloseShiftButton";
import { fmtQty } from "@/components/inventory/api";
import { WasteModal } from "@/components/inventory/WasteModal";
import { ManagerAlertsCard } from "./ManagerAlertsCard";

const hm = (h) => { const m = Math.round((h || 0) * 60); return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`; };
const since = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—");
const openBreak = (e) => (e?.breaks || []).find((b) => !b.end);
const card = "bg-white border border-stone-200 rounded-2xl overflow-hidden";
const head = "px-5 py-3 border-b border-stone-200 flex items-center gap-2 font-black text-stone-800 text-sm uppercase tracking-widest";

export function ManagerHome({ token, onSignedOut, goTo }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [tossing, setTossing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await adminApi(token).get("/manager/home");
      setData(d);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) onSignedOut?.(err?.response?.data?.detail);
      else toast.error("Couldn't refresh");
    }
  }, [token, onSignedOut]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const clock = async (action) => {
    setBusy(action);
    try {
      const { data: d } = await adminApi(token).post("/manager/clock", { action });
      toast.success({ in: "Clocked in", out: `Clocked out · ${d.punch?.hours ?? 0}h`, break_start: "Break started", break_end: "Back from break" }[action]);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update clock"); }
    finally { setBusy(""); }
  };

  const toggleOrders = async () => {
    setBusy("orders");
    try {
      await adminApi(token).put("/site-settings", { orders_open: !data.orders_open });
      toast.success(!data.orders_open ? "Online ordering is OPEN" : "Online ordering is CLOSED");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update"); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="text-center text-stone-400 py-10 text-sm">Loading…</div>;
  const st = data.clock_state;
  const pct = data.labor?.pct;
  const pctCls = pct == null ? "text-stone-400" : pct > 35 ? "text-red-600" : pct > 25 ? "text-amber-600" : "text-emerald-700";

  return (
    <div className="space-y-4" data-testid="manager-home">
      <div className="bg-stone-900 text-white rounded-3xl p-5" data-testid="manager-self-card">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Hi {data.employee.name.split(" ")[0]}</div>
            <div className="font-black text-lg flex items-center gap-2" data-testid="manager-clock-state">
              {st === "off" ? <><Clock className="w-5 h-5 text-stone-400" /> Off the clock</> : st === "break" ? <><Coffee className="w-5 h-5 text-brand-yellow" /> On break</> : <><Clock className="w-5 h-5 text-emerald-400" /> Clocked in since {since(data.entry?.clock_in)}</>}
            </div>
          </div>
          <div className="flex gap-2">
            {st === "off" ? (
              <button type="button" disabled={!!busy} onClick={() => clock("in")} data-testid="manager-clock-in" className="bg-emerald-500 text-white px-4 py-2 rounded-full text-[10px] uppercase tracking-widest font-black">Clock in</button>
            ) : (
              <>
                {st === "on" ? <button type="button" disabled={!!busy} onClick={() => clock("break_start")} data-testid="manager-break-start" className="bg-white/10 px-3 py-2 rounded-full text-[10px] uppercase tracking-widest font-black">Break</button>
                  : <button type="button" disabled={!!busy} onClick={() => clock("break_end")} data-testid="manager-break-end" className="bg-brand-yellow text-stone-900 px-3 py-2 rounded-full text-[10px] uppercase tracking-widest font-black">End break</button>}
                <button type="button" disabled={!!busy} onClick={() => clock("out")} data-testid="manager-clock-out" className="bg-brand-red text-white px-3 py-2 rounded-full text-[10px] uppercase tracking-widest font-black">Clock out</button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={card} data-testid="manager-shift-card">
        <div className={head}>Shift</div>
        <div className="p-4 flex items-center gap-3 flex-wrap">
          <button type="button" onClick={toggleOrders} disabled={busy === "orders"} data-testid="manager-orders-toggle"
            className={`flex-1 min-w-[200px] py-3 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 ${data.orders_open ? "bg-emerald-600 text-white" : "bg-stone-800 text-white"}`}>
            {data.orders_open ? <><Power className="w-4 h-4" /> Ordering open — tap to close</> : <><PowerOff className="w-4 h-4" /> Ordering closed — tap to start shift</>}
          </button>
          <CloseShiftButton token={token} onClosed={load} />
          <button type="button" onClick={() => setTossing(true)} data-testid="manager-toss-btn"
            className="py-3 px-4 rounded-2xl border-2 border-stone-200 text-stone-700 font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2"><PackageX className="w-4 h-4 text-brand-red" /> Toss it</button>
        </div>
      </div>

      <div className={card} data-testid="manager-labor-card">
        <div className={head}>Labor today</div>
        <div className="grid grid-cols-2 divide-x divide-stone-100">
          {[["Hours worked", hm(data.labor?.hours), "text-stone-900", "manager-labor-hours"], ["Labor %", pct == null ? "—" : `${pct}%`, pctCls, "manager-labor-pct"]].map(([t, v, cls, tid]) => (
            <div key={t} className="px-4 py-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{t}</div>
              <div className={`text-xl font-black tabular-nums ${cls}`} data-testid={tid}>{v}</div>
            </div>
          ))}
        </div>
        <div className="px-4 pb-3 text-[11px] text-stone-500">{data.labor?.orders || 0} orders today{pct == null ? " · labor % shows once there are sales" : " · labor cost as a share of sales"}</div>
      </div>

      <div className={card} data-testid="manager-onclock-card">
        <div className={head}><Users className="w-4 h-4 text-brand-red" /> On the clock <span className="ml-auto text-xs text-stone-500 normal-case tracking-normal font-bold">{data.on_clock.length}</span></div>
        {data.on_clock.length === 0 ? <div className="px-5 py-6 text-center text-sm text-stone-400" data-testid="manager-onclock-empty">Nobody is clocked in.</div> : (
          <ul className="divide-y divide-stone-100">
            {data.on_clock.map((e) => (
              <li key={e.id} className="px-5 py-2.5 flex items-center justify-between text-sm" data-testid={`manager-onclock-${e.employee_id}`}>
                <span className="font-bold text-stone-800">{e.employee_name}</span>
                <span className="text-xs text-stone-500 flex items-center gap-1">{openBreak(e) ? <><Coffee className="w-3.5 h-3.5 text-amber-500" /> on break</> : <>since {since(e.clock_in)}</>}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <button type="button" onClick={() => goTo("inventory", "shopping")} className={`${card} text-left`} data-testid="manager-lowstock-card">
          <div className={head}><AlertTriangle className={`w-4 h-4 ${data.low_stock.length ? "text-red-600" : "text-stone-300"}`} /> Low stock <span className="ml-auto text-xs normal-case tracking-normal font-bold text-red-600">{data.low_stock.length || ""}</span></div>
          {data.low_stock.length === 0 ? <div className="px-5 py-4 text-sm text-stone-400">All stocked up.</div> : (
            <ul className="px-5 py-2 text-sm divide-y divide-stone-100">
              {data.low_stock.slice(0, 5).map((i) => <li key={i.id} className="py-1.5 flex justify-between"><span className="font-bold text-red-700">{i.name}</span><span className="tabular-nums text-stone-600">{fmtQty(i.current_stock)} / {fmtQty(i.low_stock_threshold)} {i.unit}</span></li>)}
              <li className="py-2 text-[10px] uppercase tracking-widest font-black text-brand-red flex items-center gap-1"><ShoppingCart className="w-3.5 h-3.5" /> Open shopping list</li>
            </ul>
          )}
        </button>
        <button type="button" onClick={() => goTo("chat")} className={`${card} text-left`} data-testid="manager-chat-card">
          <div className={head}><MessageCircle className="w-4 h-4 text-brand-red" /> Customer chat</div>
          <div className="px-5 py-4 text-sm">
            {data.chat_unread > 0 ? <span className="font-black text-brand-red" data-testid="manager-chat-unread">{data.chat_unread} unread message{data.chat_unread === 1 ? "" : "s"}</span> : <span className="text-stone-400">No unread messages.</span>}
          </div>
        </button>
      </div>

      <ManagerAlertsCard token={token} />

      {tossing && <WasteModal token={token} onClose={() => setTossing(false)} onDone={() => { setTossing(false); load(); }} />}
      <button type="button" onClick={() => onSignedOut?.()} className="w-full text-[10px] uppercase tracking-widest font-black text-stone-400 flex items-center justify-center gap-1 py-2" data-testid="manager-signout-bottom"><LogOut className="w-3.5 h-3.5" /> Sign out (stays clocked in)</button>
    </div>
  );
}
